import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";

export default function ForcePasswordChange() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 12 || !/\d/.test(pw) || !/[^A-Za-z0-9]/.test(pw)) {
      setError("Password must be at least 12 characters and include at least one number and one special character");
      return;
    }
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated");
      await refresh();
      nav("/app");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-appbg px-6" data-testid="force-password-change">
      <div className="w-full max-w-md fade-up">
        <div className="flex items-center gap-2 overline text-orangeink mb-2"><ShieldCheck size={16} weight="fill" /> Secure your account</div>
        <h2 className="font-heading font-bold text-3xl tracking-tighter text-navy mb-2">Set a new password</h2>
        <p className="text-sm text-navy/70 mb-7">
          Welcome{user?.name ? `, ${user.name}` : ""}. For security, please replace your temporary password
          before continuing.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="overline text-navy/70 block mb-1.5">New password</span>
            <div className="relative">
              <input data-testid="new-password-input" type={show ? "text" : "password"} required value={pw}
                onChange={(e) => setPw(e.target.value)} className="fpc-input pr-10" placeholder="At least 12 characters" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/70 hover:text-navy">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <span className="text-navy/65 text-xs mt-1.5 block">Min 12 characters, with at least 1 number and 1 special character.</span>
          </label>
          <label className="block">
            <span className="overline text-navy/70 block mb-1.5">Confirm new password</span>
            <input data-testid="confirm-password-input" type={show ? "text" : "password"} required value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="fpc-input" placeholder="Re-enter password" />
          </label>

          {error && <div data-testid="fpc-error" className="text-negative text-sm font-medium">{error}</div>}

          <button type="submit" disabled={loading} data-testid="fpc-submit-btn"
            className="w-full bg-orange text-white font-medium py-3 rounded-lg hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            {loading ? "SAVING…" : "Update password & continue"}
          </button>
        </form>

        <button onClick={() => logout().then(() => nav("/login"))} data-testid="fpc-logout-btn"
          className="mt-5 text-sm text-navy/70 hover:text-navy">Sign out instead</button>

        <style>{`.fpc-input{width:100%;background:#fff;border:1px solid rgba(11,26,47,.2);border-radius:4px;padding:.7rem .85rem;font-size:.9rem;color:#0E0F3F;outline:none;transition:all .15s}.fpc-input:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(46,163,253,.25)}`}</style>
      </div>
    </div>
  );
}
